import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	AppBar,
	Box,
	Button,
	FormControl,
	Grid,
	Paper,
	TextField,
	Toolbar,
	Typography,
	useTheme,
} from "@material-ui/core";
import TextareaAutosize from "@material-ui/core/TextareaAutosize";

import { isNil } from "lodash-es";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AssetTitle } from "./components/AssetTitle";
import { GradeSelector } from "./components/GradeSelector";
import { LadleLifeTrimTempCheckbox } from "./components/LadleLifeCheckbox";
import { LadleSelector } from "./components/LadleSelector";
import { Notifications } from "./components/Notifications";
import { ProcessNavigator } from "./components/ProcessNavigator";
import { RefreshButtons } from "./components/RefreshButtons";
import { DurationFieldDE } from "./components/form-fields/common/DurationFieldDE";
import DurationInput from "./components/form-fields/common/DurationInput";
import { NumberTextFieldDE } from "./components/form-fields/common/NumberTextFieldDE";
import { ProcessDateTimeFieldDE } from "./components/form-fields/process/ProcessDateTimeFieldDE";
import { JustifyDelay } from "./components/modals/delay-justification/JustifyDelay";
import { TableCaveIn } from "./components/tables/TableCaveIn";
import { TableForemanEtc } from "./components/tables/TableForemanEtc";
import { useEAFDETablesStyles } from "./components/tables/styles/useEAFDETablesStyles";
import { TableAdditions } from "./components/tables/table-additions/TableAdditions";
import { TableCharges } from "./components/tables/table-charges/TableCharges";
import { TableElectrodeEvents } from "./components/tables/table-electrode-events/TableElectrodeEvents";
import { TableHeatings } from "./components/tables/table-heatings/TableHeatings";
import { TableProbes } from "./components/tables/table-probes/TableProbes";
import { TableWireAdditions } from "./components/tables/table-wire-additions/TableWireAdditions";
import { useDataEntryContext } from "./context/reducer.provider";
import { useTimerContext } from "./context/timer.provider";
import { useDelayNotificationAccess } from "./hooks/useDelayNotificationAccess";
import { useEAFDataEntryActions } from "./hooks/useEAFDataEntryActions";
import { stringTimeToMinutes } from "./utils/stringTimeToMinutes";

function decimalToTime(decimalMinutes: number | undefined) {
	const minutes = Math.floor(decimalMinutes ?? 0);
	const seconds = Math.round(((decimalMinutes ?? 0) - minutes) * 60);
	const formattedMinutes = String(minutes).padStart(2, "0");
	const formattedSeconds = String(seconds).padStart(2, "0");
	return `${formattedMinutes}:${formattedSeconds}`;
}

export const EafDataEntryPage = () => {
	const classes = useEAFDETablesStyles();
	const theme = useTheme();
	const navigate = useNavigate();
	const { pause, resume } = useTimerContext();
	const {
		state: { deProcess, unjustifiedDelays },
		actions: { setProcess, setNotificationMessage },
	} = useDataEntryContext();
	const { saveHeat, saveProcessData, refreshData } = useEAFDataEntryActions();
	const { hasAccess, delayJustificationDismissed } =
		useDelayNotificationAccess();
	const process = deProcess?.process;

	const [justifyDelayOpen, setJustifyDelayOpen] = useState(false);

	const showAppBar = useMemo(() => {
		if (!hasAccess) return false;
		return delayJustificationDismissed() || unjustifiedDelays.length > 0;
	}, [
		hasAccess,
		delayJustificationDismissed,
		unjustifiedDelays,
		process?.ProcessId,
	]);

	const handleJustifyNow = () => {
		if (unjustifiedDelays.length === 1) {
			setJustifyDelayOpen(true);
		} else {
			navigate(`/eaf-delays-manager/${unjustifiedDelays[0].ShiftId}`);
		}
	};

	useEffect(() => {
		const hasDelays = unjustifiedDelays.length > 0;
		const wasDismissed = delayJustificationDismissed();
		if (hasAccess && hasDelays && !wasDismissed) {
			setJustifyDelayOpen(true);
		}
	}, [
		hasAccess,
		unjustifiedDelays,
		delayJustificationDismissed,
		process?.ProcessId,
	]);

	return (
		<>
			{showAppBar && unjustifiedDelays.length > 0 && (
				<AppBar
					position="static"
					style={{ backgroundColor: " #f9f871" }}
					key={`Asset ${process?.AssetId} - Process ${process?.ProcessId} - Delays ${unjustifiedDelays.length}`}
				>
					<Toolbar>
						<Grid
							container
							justifyContent={"flex-start"}
							alignItems={"center"}
							spacing={2}
						>
							<Grid item xs={12} md={"auto"}>
								<Typography variant="h6" style={{ color: "#000000" }}>
									You have {unjustifiedDelays.length} unjustified delays pending{" "}
									{process?.AssetName ? `- ${process.AssetName}` : ""}
								</Typography>
							</Grid>
							<Grid item xs={12} md={"auto"}>
								<Button
									onClick={handleJustifyNow}
									variant="contained"
									color="primary"
									size="large"
								>
									Justify Now!
								</Button>
							</Grid>
						</Grid>
					</Toolbar>
				</AppBar>
			)}

			<Box padding={1} style={{ backgroundColor: "#eaebec" }}>
				<Grid container spacing={1} direction={"column"}>
					<Grid item>
						<Typography variant={"h5"} style={{ fontWeight: 600 }}>
							EAF Data Entry
						</Typography>
					</Grid>

					<Grid item>
						<Paper elevation={0}>
							<Box padding={1}>
								<Grid
									item
									container
									justifyContent={"space-between"}
									alignItems={"flex-start"}
									spacing={3}
								>
									<Grid container item direction="column" xs={12} md={2}>
										<Grid item xs={12} md={12}>
											<ProcessNavigator />
										</Grid>
										{process?.AssetName && process?.AssetId ? (
											<Grid item xs={12} md={12}>
												<AssetTitle
													assetName={process!.AssetName}
													assetId={process.AssetId}
													heatStatus={process.HeatStatusCode}
												/>
											</Grid>
										) : null}
									</Grid>
									<Grid
										key={`first-${process?.ProcessId}`}
										item
										xs={12}
										sm={6}
										md={2}
									>
										<Grid container direction={"column"} spacing={2}>
											<Grid item style={{ marginBottom: -4 }}>
												<TextField
													fullWidth
													label={"Shift"}
													variant={"outlined"}
													disabled
													value={`${dxtToLocalServerTime(
														process?.ProductionDate,
														"MM/dd/yy",
													)} ${process?.Shift} - ${process?.Crew}`}
												/>
											</Grid>
											<Grid item>
												<ProcessDateTimeFieldDE field={"Start"} />
											</Grid>
											{/* <Grid item>
												<ProcessDataDateTimeFieldDE
													field={"StartTap"}
													disabled={isNil(process?.End)}
													error={
														moment(process?.End).isBefore(
															moment(process?.StartTap),
														) ||
														moment(process?.StartTap).isBefore(
															moment(process?.Start),
														)
															? "Start Tap must be between Start and End"
															: ""
													}
												/>
											</Grid> */}
											<Grid item>
												<ProcessDateTimeFieldDE field={"End"} />
											</Grid>
										</Grid>
									</Grid>
									<Grid
										key={`sec-${process?.ProcessId}`}
										item
										xs={12}
										sm={6}
										md={2}
									>
										<Grid container direction={"column"} spacing={1}>
											<Grid item>
												<NumberTextFieldDE
													label={"Steel Weight [tons]"}
													value={
														process?.SteelWeight
															? parseFloat(process.SteelWeight.toFixed(1))
															: null
													}
													disabled={process?.HeatId ? false : true}
													onChange={(e) => {
														setProcess({
															...process,
															SteelWeight: e,
														});
													}}
													onBlur={() => {
														saveHeat("SteelWeight", process?.SteelWeight).then(
															() => {},
														);
													}}
												/>
											</Grid>
											<Grid item>
												<LadleSelector
													disabled={process?.HeatId ? false : true}
													value={process?.LadleId || null}
													onChange={(value) => {
														setProcess({
															...process,
															LadleId: value!,
														});
													}}
												/>
											</Grid>
											<Grid item>
												<LadleLifeTrimTempCheckbox
													ladleLife={{
														HeatsOnLadle: process?.HeatsOnLadle || null,
														ContactTimeOnLadle:
															process?.ContactTimeOnLadle || null,
													}}
													value={process?.ForceHeatsOnLadle || null}
													onChange={(value) => {
														saveHeat("ForceHeatsOnLadle", value);
													}}
													disabled={
														process?.HeatId || process?.LadleId ? false : true
													}
												/>
											</Grid>
											<Grid item>
												<GradeSelector
													disabled={process?.HeatId ? false : true}
													value={process?.GradeId || null}
													onChange={(value) => {
														setProcess({
															...process,
															GradeId: value,
														});
													}}
												/>
											</Grid>
										</Grid>
									</Grid>
									<Grid
										key={`third-${process?.ProcessId}`}
										item
										xs={12}
										sm={6}
										md={2}
									>
										<Grid item container direction={"column"} spacing={1}>
											<Grid item>
												<DurationFieldDE
													label={"Tap To Tap [hh:mm:ss]"}
													value={process?.TapToTap ? process?.TapToTap : null}
													unit={"minutes"}
													disabled
													onChange={(value) => {
														setProcess({
															...process,
															TapTime: Number(value),
														});
													}}
												/>
											</Grid>
											<Grid item>
												<DurationInput
													label={"Power On [hh:mm]"}
													disabled={isNil(process?.End)}
													initMinutes={process?.PowerOn || 0}
													onFocusHandler={pause}
													onBlurHandler={(e) => {
														const value = stringTimeToMinutes(
															e.target.value || "00:00",
														);
														saveProcessData({
															processId: process?.ProcessId!,
															field: "PowerOn",
															value: value || 0,
														});
														resume();
													}}
													onTimeChange={(value) => {
														setProcess({
															...process,
															PowerOn: Number(value),
														});
													}}
												/>
											</Grid>
											<Grid item>
												<TextField
													fullWidth
													disabled
													label={"Power Off [hh:mm]"}
													variant={"outlined"}
													value={
														process?.PowerOff
															? decimalToTime(process?.PowerOff / 60)
															: 0
													}
													onChange={(e) => {
														setProcess({
															...process,
															PowerOn: Number(e.target.value),
														});
													}}
												/>
											</Grid>
											<Grid item>
												<NumberTextFieldDE
													label={"Kwh / tons"}
													value={
														process?.KWHPerTon
															? parseFloat(process.KWHPerTon.toFixed(1))
															: null
													}
													onChange={(value) => {
														setProcess({
															...process,
															KWHPerTon: Number(value),
														});
													}}
													onBlur={() => {
														saveProcessData({
															processId: process?.ProcessId!,
															field: "KWHPerTon",
															value: process?.KWHPerTon
																? process?.KWHPerTon
																: 0,
														});
													}}
												/>
											</Grid>
										</Grid>
									</Grid>
									<Grid
										key={`fourth-${process?.ProcessId}`}
										item
										xs={12}
										sm={12}
										md={4}
										style={{ marginTop: 5 }}
									>
										<Grid item container direction={"column"} spacing={2}>
											<Grid item xs={12} sm={12} md={6}>
												<RefreshButtons />
											</Grid>
											<Grid item xs={12} sm={12} md={6}>
												<Button
													fullWidth
													variant={"contained"}
													style={{
														backgroundColor: theme.palette.info.main,
														color: theme.palette.info.contrastText,
													}}
													size="large"
													onClick={() => {
														const shiftId = process?.ShiftId;
														if (shiftId) {
															navigate(`/eaf-processes/${shiftId}`);
														}
													}}
												>
													Processes
												</Button>
											</Grid>
											<Grid item xs={12} sm={12} md={6}>
												<Button
													fullWidth
													variant={"contained"}
													size="large"
													disabled={!process?.HeatShiftId || !process?.HeatId}
													style={{
														backgroundColor: theme.palette.info.main,
														color: theme.palette.info.contrastText,
													}}
													onClick={() => {
														const heatId = process?.HeatId;
														const heatShiftId = process?.HeatShiftId;
														if (heatId) {
															navigate(
																`/meltshop-heats/${heatShiftId}?heatId=${heatId}`,
															);
														}
													}}
												>
													Heats Page
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Paper>
					</Grid>
					<Grid item container spacing={1}>
						<Grid item xs={12} md={4}>
							<Grid container direction={"column"} spacing={1}>
								<Grid item xs={12} md={12}>
									<TableProbes />
								</Grid>
								<Grid item xs={12} md={12}>
									<TableAdditions />
								</Grid>
							</Grid>
						</Grid>

						<Grid item xs={12} md={4}>
							<Grid container direction={"column"} spacing={1}>
								<Grid item xs={12} md={12}>
									<Paper elevation={0}>
										<Box padding={1} className={`${classes.middleTextfields}`}>
											<Grid
												container
												spacing={1}
												style={{ justifyContent: "center" }}
											>
												<Grid item xs={12} md={6}>
													<TableForemanEtc />
												</Grid>
												<Grid item xs={12} md={6}>
													<TableCaveIn />
												</Grid>
											</Grid>
										</Box>
									</Paper>
								</Grid>
								<Grid item xs={12} md={12}>
									<TableHeatings />
								</Grid>
							</Grid>
						</Grid>
						<Grid item xs={12} md={4}>
							<Grid container direction={"column"} spacing={1}>
								<Grid item xs={12} md={12}>
									<TableCharges />
								</Grid>
								<Grid item xs={12} md={12}>
									<TableElectrodeEvents />
								</Grid>
								<Grid item xs={12} md={12}>
									<TableWireAdditions />
								</Grid>
								{/* <Grid item xs={12} md={12}>
									<TablePourbacks />
								</Grid> */}
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<Paper>
							<Box padding={1}>
								<FormControl>
									<TextareaAutosize
										className={classes.outlinedTextarea}
										placeholder="Notes"
										minRows={4}
										style={{ width: "100%" }}
										value={process?.Comments ?? ""}
										onChange={(e) => {
											setProcess({
												...process,
												Comments: e.target.value,
											});
										}}
										onBlur={() => {
											saveProcessData({
												processId: process?.ProcessId!,
												field: "Comments",
												value: process?.Comments ?? "",
											}).then(() => {});
										}}
										onFocus={() => pause()}
									/>
								</FormControl>
							</Box>
						</Paper>
					</Grid>
				</Grid>
			</Box>

			<Notifications />
			{unjustifiedDelays.length === 1 && process?.AssetId && (
				<JustifyDelay
					open={justifyDelayOpen}
					onHide={(justifiedDelay?: boolean) => {
						setJustifyDelayOpen(false);
						resume();
						if (justifiedDelay) {
							setNotificationMessage("Delay justified successfully.");
						}
					}}
					delay={unjustifiedDelays[0]}
					assetId={process?.AssetId}
					refreshData={refreshData}
				/>
			)}
		</>
	);
};

export default EafDataEntryPage;
